import type React from 'react';
import { useEffect, useState } from 'react';
import { listUserRoles, assignUserRole, revokeUserRole, type AdminUserRole } from '../../lib/adminApi';
import { useRole } from '../../lib/roleContext';
import { AdminPageShell, cls } from './shared';

export function UsersPage() {
  const { role: myRole } = useRole();
  const [users, setUsers] = useState<AdminUserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'design'>('design');
  const [saving, setSaving] = useState(false);

  function load() {
    listUserRoles()
      .then((r) => {
        setUsers(r.users);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleAssign() {
    if (!newIdentifier.trim()) return;
    setSaving(true);
    try {
      await assignUserRole({ identifier: newIdentifier.trim(), role: newRole });
      setNewIdentifier('');
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error assigning role');
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(oid: string) {
    if (!confirm('Revoke this user\'s elevated role?')) return;
    try {
      await revokeUserRole(oid);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error revoking role');
    }
  }

  if (loading) return <AdminPageShell title="User Roles"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="User Roles"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  return (
    <AdminPageShell title="User Roles">
      <div className={cls.formPanel}>
        <p className="text-sm font-medium text-vari-navy mb-3">Assign Role</p>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs text-vari-gray block mb-1">Vari email or Entra OID</label>
            <input
              className={cls.input}
              placeholder="name@vari.com or xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={newIdentifier}
              onChange={(e) => setNewIdentifier(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-vari-gray block mb-1">Role</label>
            <select
              className={cls.select}
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as 'admin' | 'design')}
            >
              <option value="design">design</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <button
            onClick={() => void handleAssign()}
            disabled={saving || !newIdentifier.trim()}
            className={cls.btnSmPrimary}
          >
            {saving ? 'Saving…' : 'Assign'}
          </button>
        </div>
        <p className="text-xs text-vari-gray mt-3">
          Enter a <strong>@vari.com email</strong> to assign by email (works before first sign-in), or an{' '}
          <strong>Entra OID</strong> for precision. Find an OID at{' '}
          <a href="https://jwt.ms" target="_blank" rel="noopener noreferrer" className="underline">
            jwt.ms
          </a>{' '}
          after signing in.
        </p>
      </div>

      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>User</th>
              <th className={cls.th}>Role</th>
              <th className={cls.th}>Assigned by</th>
              <th className={cls.th}>Date</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.oid} className={cls.tr(i % 2 === 1)}>
                <td className={u.oid.includes('@') ? cls.td : cls.tdMono}>
                  {u.oid.includes('@') ? u.oid : u.oid}
                </td>
                <td className={cls.td}>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'admin' ? 'bg-vari-navy/10 text-vari-navy' : 'bg-vari-yellow/30 text-vari-navy'}`}>
                    {u.role}
                  </span>
                </td>
                <td className={u.assigned_by.includes('@') ? cls.td : cls.tdMono}>
                  {u.assigned_by}
                </td>
                <td className={`${cls.td} text-vari-gray`}>
                  {new Date(u.assigned_at).toLocaleDateString()}
                </td>
                <td className={cls.tdRight}>
                  {myRole === 'admin' && (
                    <button
                      onClick={() => void handleRevoke(u.oid)}
                      className={cls.btnDanger}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No elevated users yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminPageShell>
  );
}
